import {
  isBefore,
  isAfter,
  addMinutes,
  format,
  setHours,
  setMinutes,
  startOfMinute,
  parse,
} from 'date-fns'
import i18n from 'i18next'

import { jstNewDate } from 'ui/utils/jstDatetime'
import getDateLocale from 'ui/utils/getDateLocale'

import { TimeString, DatetimeSecondString } from 'config/format'
import type { ReservationTimeUnit } from 'types/common'
import { defaultReservationTimeUnit } from 'config/reservation'

export const toDateDayTimeString: (date: Date) => string = (date) =>
  format(date, i18n.t('datetime.yyyy年M月d日 E曜H:mm'), {
    locale: getDateLocale(),
  })

export const toDateDayTimeDetailString: (date: Date) => string = (date) =>
  format(date, i18n.t('datetime.yyyy年M月d日 EEEEH:mm'), {
    locale: getDateLocale(),
  })

export const toDateDayTimeStringFromDateTimeString: (
  datetimeString: string,
  long?: boolean
) => string = (datetimeString, long = false) => {
  const date = parse(datetimeString, DatetimeSecondString, new Date())
  return long ? toDateDayTimeDetailString(date) : toDateDayTimeString(date)
}

export type StartEnd = { start: string; end: string }

export const buildStartEndList: (settings: {
  from?: string
  to?: string
  unit: number
}) => StartEnd[] = ({ from = '00:00', to = '24:00', unit }) => {
  const [fromHour, fromMin] = from.split(':').map((string) => Number(string))
  const [toHour, toMin] = to.split(':').map((string) => Number(string))
  const min = startOfMinute(setMinutes(setHours(jstNewDate, fromHour), fromMin))
  const max = startOfMinute(setMinutes(setHours(jstNewDate, toHour), toMin))
  if (isBefore(max, min)) return []
  let start = new Date(min)
  let array = []
  while (!isAfter(addMinutes(start, unit), max)) {
    let end = format(addMinutes(start, unit), TimeString)
    // NOTE: 日付の概念がないので便宜上24:00にする,サーバーでは内部で00:00に変換して保存される
    if (end === '00:00') {
      end = '24:00'
    }
    array.push({
      start: format(start, TimeString),
      end: end,
    })
    start = addMinutes(start, unit)
  }
  return array
}

export const timeStringToNumber = (string: string) =>
  Number(string.replace(':', ''))

function addMinutesStringArr(
  hour: number,
  timeUnit: ReservationTimeUnit
): string[] {
  const quotient = 60 / timeUnit
  const hourString = `00${hour}`.slice(-2)
  return [...Array(quotient)].map(
    (_, number) => `${hourString}:${`00${number * timeUnit}`.slice(-2)}`
  )
}

function allTimeStringArr(timeUnit: ReservationTimeUnit): string[] {
  let arr: string[] = []
  Array.from(new Array(24)).forEach((_, index) => {
    arr.push(...addMinutesStringArr(index, timeUnit))
  })
  arr.push('24:00')
  return arr
}

export class AllTimeStringArray {
  protected all: string[]
  constructor(timeUnit: ReservationTimeUnit = defaultReservationTimeUnit) {
    this.all = allTimeStringArr(timeUnit)
  }
  start(end?: string | null): string[] {
    const arr = [...this.all]
    arr.pop()
    return end
      ? arr.filter(
          (start) => timeStringToNumber(start) < timeStringToNumber(end)
        )
      : arr
  }
  end(start?: string | null): string[] {
    const arr = [...this.all]
    arr.shift()
    return start
      ? arr.filter((end) => timeStringToNumber(start) < timeStringToNumber(end))
      : arr
  }
}
